import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { SideFilter } from '_/components/side-filter'
import { BasicInput, Select } from '_/components/inputs'
import { DatePickerInterval } from '_/components/date-picker-interval'

import { getProfileList } from '_/modules/profile/actions'
import { getProfilesSelector } from '_/modules/profile/selectors'
import { getAreaOfActivityList } from '_/modules/areaOfActivity/actions'
import { areasOfActivityListSelector } from '_/modules/areaOfActivity/selectors'

import classNames from 'classnames'
import { useToast } from '_/hooks/use-toast'

import useStyles from './styles'
import { formatDateIntervalFilter } from '../utils'

const DEFAULT_ALL_OPTION = { label: '(Todos)', value: 'all' }

const DEFAULT_SELECT_OPTIONS = [
  DEFAULT_ALL_OPTION,
  { label: 'Sim', value: 'true' },
  { label: 'Não', value: 'false' },
]

function formatSelectOptions({ arr, label = 'name', value = 'id' }) {
  const formattedOptions =
    arr?.map(item => ({
      label: item?.[label],
      value: item?.[value],
    })) || []
  return [DEFAULT_ALL_OPTION, ...formattedOptions]
}

export function WidgetFilter({ open, close, filters, setFilters, applyFilters }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()

  const profilesList = useSelector(getProfilesSelector)
  const areaOfActivityList = useSelector(areasOfActivityListSelector)

  const [loading, setLoading] = useState(false)

  const methods = useForm({ mode: 'all' })

  const { watch, setValue, errors, register, reset, handleSubmit } = methods

  const activeProfileOptions = useMemo(() => {
    return formatSelectOptions({ arr: profilesList?.results, label: 'desc' })
  }, [profilesList])

  const areaOfActivityOptions = useMemo(() => {
    return formatSelectOptions({ arr: areaOfActivityList })
  }, [areaOfActivityList])

  const handleFilters = data => {
    const { createdAtStart = '', createdAtEnd = '' } = data

    const createdAt = formatDateIntervalFilter(createdAtStart, createdAtEnd)

    const filterParams = {
      ...data,
      createdAt,
    }

    applyFilters(filterParams)
    close()
  }

  const handleChange = (value, name) => setValue(name, value)

  const formatDefaultValues = () => {
    register('createdAtStart')
    register('createdAtEnd')
    setValue('createdAtStart', filters?.createdAtStart ?? null)
    setValue('createdAtEnd', filters?.createdAtEnd ?? null)
  }

  async function fetchOptions() {
    setLoading(true)

    try {
      if (!profilesList?.results?.length) await dispatch(getProfileList())
      if (!areaOfActivityList?.length) await dispatch(getAreaOfActivityList())
    } catch (err) {
      console.error('Erro ao carregar opções dos filtros:', err)
      showToast({
        type: 'error',
        message: 'Erro ao carregar opções dos filtros. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }
  const resetFilters = () => {
    reset()
    setFilters(watch())
    formatDefaultValues()
  }

  useEffect(() => {
    formatDefaultValues()
  }, [filters])

  useEffect(() => {
    if (open) fetchOptions()
  }, [open])

  const mainFilters = useMemo(() => {
    return (
      <>
        <BasicInput
          name="title"
          placeholder="Nome"
          label="Nome contenha"
          defaultValue={filters?.title || ''}
          labelClasses={styles.inputLabel}
          style={{ margin: '16px 0 -16px' }}
        />

        <div className={styles.selectContainer}>
          <Select
            label="Perfil"
            disabled={loading}
            name="activeProfile"
            defaultValue={filters?.activeProfile || 'all'}
            labelClasses={classNames(styles.inputLabel, styles.selectLabel)}
            options={activeProfileOptions}
            getKey={item => item?.value}
            getValue={item => item?.value}
            getLabel={item => item?.label}
          />
        </div>

        <div className={styles.selectContainer}>
          <Select
            disabled={loading}
            name="areaOfActivity"
            label="Área de atividade"
            defaultValue={filters?.areaOfActivity || 'all'}
            labelClasses={classNames(styles.inputLabel, styles.selectLabel)}
            options={areaOfActivityOptions}
            getKey={item => item?.value}
            getValue={item => item?.value}
            getLabel={item => item?.label}
          />
        </div>

        <BasicInput
          name="agency"
          label="Intermediária"
          placeholder="Intermediária"
          defaultValue={filters?.agency || ''}
          labelClasses={styles.inputLabel}
          style={{ margin: '16px 0 -16px' }}
        />

        <BasicInput
          name="sql"
          label="SQL contenha"
          placeholder="SQL"
          defaultValue={filters?.sql || ''}
          labelClasses={styles.inputLabel}
          style={{ margin: '16px 0 -16px' }}
        />

        <DatePickerInterval
          name="createdAt"
          label="Criado entre"
          localState={watch()}
          handleChange={handleChange}
          disableFuture
          firstError={errors?.createdAtStart?.message}
          secondError={errors?.createdAtEnd?.message}
          labelClasses={classNames(styles.inputLabel, styles.datePickerLabel)}
        />

        <div className={styles.selectContainer}>
          <Select
            label="Ativo"
            name="active"
            defaultValue={filters?.active || 'all'}
            labelClasses={classNames(styles.inputLabel, styles.selectLabel)}
            options={DEFAULT_SELECT_OPTIONS}
            getKey={item => item.value}
            getValue={item => item.value}
            getLabel={item => item.label}
          />
        </div>
      </>
    )
  }, [{ ...methods }, loading])

  useEffect(() => {
    formatDefaultValues()
  }, [])

  return (
    <FormProvider {...methods}>
      <SideFilter
        open={open}
        close={close}
        mainFilters={mainFilters}
        cleanFilters={resetFilters}
        applyFilters={handleSubmit(handleFilters)}
      />
    </FormProvider>
  )
}
